import { IonItem, IonPage, IonHeader, IonToolbar, IonTitle, IonButtons, IonButton, IonIcon, IonContent, IonListHeader, IonLabel, IonList, IonText, IonSpinner, IonAvatar, useIonAlert } from "@ionic/react";
import { close, shareOutline, trashOutline } from 'ionicons/icons';
import { useDeleteDeviceUserAccessMutation, useDeviceUsersQuery } from "../query/deviceUsersQuery";
import useUX from "../utils/ux";
import { Share } from "@capacitor/share";
import { useCreateDeviceUserInviteMutation } from "../query/deviceUserInviteQuery";
import { useTranslation } from "../hooks/useTranslation";

function DeviceUsersModal({ dismiss, deviceId }: { dismiss: (data?: string | null | undefined | number, role?: string) => void, deviceId: string }) {
    const { data: users, isLoading, isError } = useDeviceUsersQuery(deviceId);
    const deleteDeviceUserAccessMutation = useDeleteDeviceUserAccessMutation(deviceId);
    const { presentResult } = useUX();
    const createDeviceUserInviteMutation = useCreateDeviceUserInviteMutation(deviceId);
    const { t } = useTranslation('modals');
    const { t: tCommon } = useTranslation('common');
    const [presentAlert] = useIonAlert();

    const handleShareDevice = () => {
        presentResult(async () => {
            const result = await createDeviceUserInviteMutation.mutateAsync();
            const inviteLink = `https://onlycat.app/account/invite/${result.inviteToken}`;

            Share.share({
                title: t('device_users.share.title'),
                text: t('device_users.share.text'),
                url: inviteLink,
                dialogTitle: t('device_users.share.dialog_title')
            }).catch(error => console.log(error));
        }, t('device_users.invite_link_created'));
    };

    const confirmRemoveUser = (userId: number, userName?: string | null) => {
        const displayName = userName || t('device_users.default_user_name', { userId });

        presentAlert({
            header: t('device_users.remove_access.header'),
            subHeader: t('device_users.remove_access.sub_header_named', { name: displayName }),
            message: t('device_users.remove_access.message'),
            buttons: [
                {
                    text: tCommon('buttons.cancel'),
                    role: 'cancel'
                },
                {
                    text: tCommon('buttons.delete'),
                    role: 'destructive',
                    handler: () => {
                        presentResult(
                            () => deleteDeviceUserAccessMutation.mutateAsync(userId),
                            t('device_users.remove_access.success')
                        );
                    }
                }
            ]
        });
    };

    return (
        <IonPage>
            <IonHeader>
                <IonToolbar>
                    <IonTitle>{t('device_users.title')}</IonTitle>
                    <IonButtons slot="end">
                        <IonButton strong={true} onClick={() => dismiss(null, 'cancel')}>
                            <IonIcon slot="icon-only" icon={close} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>
            <IonContent>
                <IonList>
                    <IonListHeader>
                        <IonLabel>{t('device_users.list_header')}</IonLabel>
                    </IonListHeader>

                    {isLoading && (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                            <IonSpinner />
                        </div>
                    )}

                    {isError && (
                        <IonItem>
                            <IonText color="danger">{t('device_users.error_loading')}</IonText>
                        </IonItem>
                    )}

                    {users && users.length === 0 && (
                        <IonItem>
                            <IonText>{t('device_users.no_users')}</IonText>
                        </IonItem>
                    )}

                    {users && users.map((user) => (
                        <IonItem key={user.id}>
                            <IonAvatar slot="start">
                                <img src={user.avatarUrl || '/assets/account/default-avatar.svg'}
                                    alt={user.name || t('device_users.default_user_name', { userId: user.id })}
                                    referrerPolicy="no-referrer" />
                            </IonAvatar>
                            <IonLabel>
                                <h2>{user.name || t('device_users.default_user_name', { userId: user.id })}</h2>
                            </IonLabel>
                            <IonButton
                                slot="end"
                                fill="clear"
                                size="default"
                                disabled={deleteDeviceUserAccessMutation.isPending}
                                onClick={() => confirmRemoveUser(user.id, user.name)}
                                aria-label={t('device_users.remove_access.aria_label', { name: user.name || t('device_users.default_user_name', { userId: user.id }) })}
                            >
                                <IonIcon slot="icon-only" color="danger" icon={trashOutline} />
                            </IonButton>
                        </IonItem>
                    ))}
                </IonList>

                <div style={{ padding: '20px' }}>
                    <IonButton expand="block" onClick={handleShareDevice} disabled={createDeviceUserInviteMutation.isPending}>
                        <IonIcon slot="start" icon={shareOutline} />
                        {createDeviceUserInviteMutation.isPending ? t('device_users.generating_link') : t('device_users.share_device_access')}
                    </IonButton>
                </div>
            </IonContent>
        </IonPage>
    );
}

export default DeviceUsersModal;
